import { Link, useNavigate } from 'react-router-dom';
import { DropdownMenuItem } from '@c2fo/liquidity';
import { EyeIcon, PencilIcon } from '@c2fo/liquidity/icons';
import { useLocationsState } from '../../contexts';
import { StorageTypeChip } from '../StorageTypeChip';
import { UriLink } from '../UriLink';
import type { LocationsSortField } from '../../contexts/FileDeliveryContext';
import { TableHeader } from '../TableHeader';
import { DataTable, DataTableHead, DataTableBody, DataTableRow, DataTableCell } from '../DataTable';
import { ActionsMenu } from '../ActionsMenu';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';

export const LocationTable: React.FC = () => {
  const navigate = useNavigate();
  const { locations, sortField, sortDirection, setSort } = useLocationsState();

  const handleSort = (field: LocationsSortField) => {
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSort(field, newDirection);
    } else {
      setSort(field, 'asc');
    }
  };

  return (
    <DataTable>
      <DataTableHead>
        <TableHeader
          field="display_name"
          label="Name"
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          onSort={handleSort}
        />
        <TableHeader label="Type" align="center" className="w-24" />
        <TableHeader
          field="uri"
          label="Location URI"
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          onSort={handleSort}
        />
        <TableHeader label="Credential" />
        <TableHeader
          field="create_time"
          label="Created"
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          onSort={handleSort}
          className="w-40"
        />
        <TableHeader
          field="update_time"
          label="Updated"
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          onSort={handleSort}
          className="w-40"
        />
        <TableHeader label="" className="w-16" />
      </DataTableHead>
      <DataTableBody>
        {locations.map((location) => (
          <DataTableRow key={location.uid}>
            <DataTableCell variant="primary">
              <div className="flex flex-col">
                <span>{location.displayName}</span>
                {location.description && <span className="text-xs text-gray-500">{location.description}</span>}
              </div>
            </DataTableCell>
            <DataTableCell align="center">
              <StorageTypeChip storageType={location.storageType} />
            </DataTableCell>
            <DataTableCell variant="secondary">
              <UriLink uri={location.uri} />
            </DataTableCell>
            <DataTableCell>
              {location.credential ? (
                <Link
                  to="/file-delivery/credentials"
                  className="text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {location.credential.displayName}
                </Link>
              ) : (
                <span className="text-gray-400">None</span>
              )}
            </DataTableCell>
            <DataTableCell variant="date">
              {location.createTime ? formatDateWithTimezone(location.createTime) : '-'}
            </DataTableCell>
            <DataTableCell variant="date">
              {location.updateTime ? formatDateWithTimezone(location.updateTime) : '-'}
            </DataTableCell>
            <DataTableCell align="center">
              <ActionsMenu>
                <DropdownMenuItem
                  className="active:bg-lightBlue-100"
                  onSelect={() => navigate(`/file-delivery/${location.name}`)}
                >
                  <EyeIcon className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="active:bg-lightBlue-100"
                  onSelect={() => navigate(`/file-delivery/${location.name}/edit`)}
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              </ActionsMenu>
            </DataTableCell>
          </DataTableRow>
        ))}
      </DataTableBody>
    </DataTable>
  );
};
